import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { profile, stats } from "../../data/portfolio";
import { EASE } from "./Reveal";

const lines = ["Evidence,", "translated into", "healthier lives."];

function MaskedLine({ text, i }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay: 0.45 + i * 0.14 }}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <div className="absolute rounded-full mix-blend-multiply blur-3xl opacity-60 bg-sage w-[34rem] h-[34rem] -top-24 -left-32 pointer-events-none" />
      <div className="absolute rounded-full mix-blend-multiply blur-3xl opacity-50 bg-blush w-[30rem] h-[30rem] top-1/3 -right-32 pointer-events-none" />
      <div className="absolute rounded-full mix-blend-multiply blur-3xl opacity-40 bg-stone w-96 h-96 bottom-0 left-1/3 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="eyebrow mb-7 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block w-10 h-px bg-fog/50" />
            {profile.title}
          </motion.p>

          <h1 data-testid="hero-heading" className="font-display font-light tracking-tighter leading-[0.98] text-ink text-[13.5vw] sm:text-7xl lg:text-[5.6rem]">
            {lines.map((l, i) => (
              <MaskedLine key={l} text={l} i={i} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-fog"
            data-testid="hero-subtext"
          >
            I'm <span className="text-ink font-medium">Dr. Vishakha Dhivar</span> — a physician and public health
            researcher turning rigorous evidence into policy, equity and better care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-email-button"
              href={`mailto:${profile.email}`}
              className="group inline-flex items-center gap-2 rounded-full bg-ink text-paper px-8 py-4 text-sm font-medium tracking-wide transition-[background-color,transform] duration-300 hover:bg-[#3d453f] hover:-translate-y-0.5"
            >
              Start a conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              data-testid="hero-linkedin-button"
              href={profile.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="glass glass-hover inline-flex items-center rounded-full px-8 py-4 text-sm font-medium tracking-wide text-ink"
            >
              LinkedIn profile
            </a>
            <span data-testid="hero-location" className="inline-flex items-center gap-1.5 text-sm text-fog ml-1">
              <MapPin className="w-4 h-4" /> {profile.location}
            </span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.55 }}
            className="relative mx-auto w-64 sm:w-80 lg:w-full max-w-sm"
          >
            <div className="rounded-t-full rounded-b-[2.5rem] overflow-hidden border border-white/60 shadow-[0_30px_80px_rgba(45,51,47,0.14)] bg-stone/40">
              <motion.img
                style={{ y: imgY, scale: 1.12 }}
                src="/portrait.jpg"
                alt="Portrait of Dr. Vishakha Dhivar"
                data-testid="hero-portrait"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.35 }}
              className="glass absolute -left-2 sm:-left-12 -bottom-4 sm:bottom-10 px-5 py-4"
              data-testid="hero-stat-chip"
            >
              <p className="font-display text-3xl font-medium text-ink leading-none">{stats[1].value}</p>
              <p className="mt-1 text-[11px] font-medium tracking-[0.14em] uppercase text-fog">{stats[1].label}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.5 }}
              className="glass absolute -right-2 sm:-right-10 -bottom-4 sm:bottom-auto sm:top-14 px-5 py-3.5"
              data-testid="hero-mph-chip"
            >
              <p className="text-xs font-semibold tracking-[0.16em] uppercase text-ink">MPH · University of Exeter</p>
              <p className="mt-0.5 text-[11px] text-fog">Systematic reviews · R · PRISMA</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
